<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'

interface ConfirmationDetail {
  label: string
  value: string
  highlight?: boolean
  wide?: boolean
}

const props = withDefaults(defineProps<{
  open: boolean
  title: string
  description?: string
  eyebrow?: string
  details?: ConfirmationDetail[]
  note?: string
  error?: string
  confirmLabel?: string
  pendingLabel?: string
  cancelLabel?: string
  pending?: boolean
}>(), {
  description: '',
  eyebrow: '確認操作',
  details: () => [],
  note: '',
  error: '',
  confirmLabel: '確認',
  pendingLabel: '處理中…',
  cancelLabel: '取消',
  pending: false,
})

const emit = defineEmits<{
  confirm: []
  cancel: []
}>()

const dialog = ref<HTMLElement | null>(null)
const cancelButton = ref<HTMLButtonElement | null>(null)
const titleId = useId()
const descriptionId = useId()
let returnFocus: HTMLElement | null = null
let previousBodyOverflow = ''
let dialogActive = false

const focusableSelector = [
  'button:not(:disabled)',
  '[href]',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function cancel() {
  if (!props.pending) emit('cancel')
}

function confirm() {
  if (!props.pending) emit('confirm')
}

function handleDocumentKeydown(event: KeyboardEvent) {
  if (!props.open) return
  if (event.key === 'Escape') {
    event.preventDefault()
    event.stopPropagation()
    cancel()
    return
  }
  if (event.key !== 'Tab' || !dialog.value) return

  const focusable = Array.from(dialog.value.querySelectorAll<HTMLElement>(focusableSelector))
  if (!focusable.length) {
    event.preventDefault()
    dialog.value.focus()
    return
  }

  const first = focusable[0]
  const last = focusable.at(-1)
  if (!first || !last) return
  if (!dialog.value.contains(document.activeElement)) {
    event.preventDefault()
    const focusTarget = event.shiftKey ? last : first
    focusTarget.focus()
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

async function activateDialog() {
  if (dialogActive) return
  dialogActive = true
  returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
  previousBodyOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  document.addEventListener('keydown', handleDocumentKeydown)
  await nextTick()
  if (props.open) cancelButton.value?.focus()
}

async function deactivateDialog(restoreFocus = true) {
  if (!dialogActive) return
  dialogActive = false
  document.removeEventListener('keydown', handleDocumentKeydown)
  document.body.style.overflow = previousBodyOverflow
  const focusTarget = returnFocus
  returnFocus = null
  if (!restoreFocus) return
  await nextTick()
  if (focusTarget?.isConnected) focusTarget.focus()
}

watch(() => props.open, (open) => {
  if (open) void activateDialog()
  else void deactivateDialog()
}, { flush: 'post', immediate: true })

onBeforeUnmount(() => {
  if (!dialogActive) return
  dialogActive = false
  document.removeEventListener('keydown', handleDocumentKeydown)
  document.body.style.overflow = previousBodyOverflow
  if (returnFocus?.isConnected) returnFocus.focus()
})
</script>

<template>
  <Teleport to="body">
    <Transition name="confirm-dialog">
      <div v-if="open" class="confirm-dialog-backdrop" @click.self="cancel">
        <section
          ref="dialog"
          class="confirm-dialog"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          :aria-describedby="description ? descriptionId : undefined"
          tabindex="-1"
        >
          <header class="confirm-dialog__header">
            <span class="eyebrow">{{ eyebrow }}</span>
            <h2 :id="titleId">{{ title }}</h2>
            <p v-if="description" :id="descriptionId">{{ description }}</p>
          </header>

          <div class="confirm-dialog__body">
            <dl v-if="details.length" class="confirm-dialog__details">
              <div v-for="detail in details" :key="detail.label" :class="{ 'confirm-dialog__detail--wide': detail.wide }">
                <dt>{{ detail.label }}</dt>
                <dd :class="{ 'confirm-dialog__value--highlight': detail.highlight }">{{ detail.value }}</dd>
              </div>
            </dl>
            <slot />
            <p v-if="note" class="confirm-dialog__note">{{ note }}</p>
            <p v-if="error" class="confirm-dialog__error" role="alert">{{ error }}</p>
          </div>

          <footer class="confirm-dialog__footer">
            <button ref="cancelButton" class="button button--quiet" type="button" :disabled="pending" @click="cancel">
              {{ cancelLabel }}
            </button>
            <button class="button button--trade" type="button" :disabled="pending" :aria-busy="pending" @click="confirm">
              {{ pending ? pendingLabel : confirmLabel }}
            </button>
          </footer>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.confirm-dialog-backdrop {
  position: fixed;
  z-index: 120;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  padding: 24px;
  background: rgba(1, 6, 12, .8);
  backdrop-filter: blur(8px);
  overscroll-behavior: contain;
}

.confirm-dialog {
  width: min(520px, 100%);
  min-width: 0;
  max-height: calc(100dvh - 48px);
  overflow-x: hidden;
  overflow-y: auto;
  border: 1px solid rgba(114, 166, 255, .35);
  border-radius: 18px;
  background: var(--surface);
  box-shadow: 0 30px 100px rgba(0, 0, 0, .58);
}

.confirm-dialog:focus { outline: none; }
.confirm-dialog__header { padding: 24px 24px 18px; border-bottom: 1px solid var(--line); }
.confirm-dialog__header h2 { margin-top: 7px; font-size: 1.35rem; }
.confirm-dialog__header p { margin-top: 9px; color: var(--muted); font-size: .78rem; line-height: 1.6; }
.confirm-dialog__body { min-width: 0; padding: 20px 24px 24px; }
.confirm-dialog__details { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1px; overflow: hidden; margin: 0; border: 1px solid var(--line); border-radius: 12px; background: var(--line); }
.confirm-dialog__details > div { min-width: 0; padding: 14px; background: rgba(5, 14, 24, .8); }
.confirm-dialog__detail--wide { grid-column: 1 / -1; }
.confirm-dialog__details dt { margin-bottom: 7px; color: var(--faint); font-size: .66rem; font-weight: 750; }
.confirm-dialog__details dd { margin: 0; overflow-wrap: anywhere; color: var(--text); font-size: .88rem; font-weight: 760; font-variant-numeric: tabular-nums; }
.confirm-dialog__details .confirm-dialog__value--highlight { color: #a9c9ff; font-size: 1rem; }
.confirm-dialog__note { margin-top: 14px; padding: 12px 13px; border: 1px solid rgba(114, 166, 255, .2); border-radius: 10px; background: rgba(114, 166, 255, .08); color: #c8daf6; font-size: .72rem; line-height: 1.55; }
.confirm-dialog__error { margin-top: 14px; padding: 12px 13px; border: 1px solid rgba(255, 130, 144, .3); border-radius: 10px; background: rgba(255, 130, 144, .09); color: #ffadb6; font-size: .75rem; line-height: 1.55; }
.confirm-dialog__footer { display: grid; grid-template-columns: 1fr 1.3fr; gap: 12px; padding: 18px 24px 24px; border-top: 1px solid var(--line); }
.confirm-dialog__footer .button { width: 100%; min-height: 48px; }

.confirm-dialog-enter-active,
.confirm-dialog-leave-active { transition: opacity .18s ease; }
.confirm-dialog-enter-active .confirm-dialog,
.confirm-dialog-leave-active .confirm-dialog { transition: transform .18s ease, opacity .18s ease; }
.confirm-dialog-enter-from,
.confirm-dialog-leave-to { opacity: 0; }
.confirm-dialog-enter-from .confirm-dialog,
.confirm-dialog-leave-to .confirm-dialog { opacity: 0; transform: translateY(10px) scale(.985); }

@media (max-width: 620px) {
  .confirm-dialog-backdrop { align-items: flex-end; padding: 0; }
  .confirm-dialog {
    width: 100%;
    max-width: 100%;
    max-height: min(88dvh, 720px);
    border-right: 0;
    border-bottom: 0;
    border-left: 0;
    border-radius: 20px 20px 0 0;
    padding-bottom: env(safe-area-inset-bottom);
  }
  .confirm-dialog__header { padding: 20px 18px 16px; }
  .confirm-dialog__body { padding: 16px 18px 18px; }
  .confirm-dialog__details { grid-template-columns: 1fr 1fr; }
  .confirm-dialog__details > div { padding: 12px; }
  .confirm-dialog__footer { gap: 10px; padding: 14px 18px 18px; }
  .confirm-dialog-enter-from .confirm-dialog,
  .confirm-dialog-leave-to .confirm-dialog { transform: translateY(100%); }
}

@media (max-width: 360px) {
  .confirm-dialog__details { grid-template-columns: 1fr; }
  .confirm-dialog__detail--wide { grid-column: auto; }
  .confirm-dialog__footer { grid-template-columns: 1fr; }
}
</style>
